import { useEffect, useState, useCallback } from "react";
import { X, Trophy, DollarSign, XCircle, Info } from "lucide-react";
import { fmtUsd } from "@/lib/wallet";

const API = process.env.REACT_APP_BACKEND_URL;
const ACK_KEY = "ufo_room_results_ack";

function getAck() { try { return new Set(JSON.parse(localStorage.getItem(ACK_KEY) || "[]")); } catch { return new Set(); } }
function saveAck(set) { localStorage.setItem(ACK_KEY, JSON.stringify([...set])); }

export default function TournamentResultModal({ user }) {
  const [result, setResult] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const r = await fetch(`${API}/api/rooms/me/recent-results?limit=5`, { credentials: "include" });
      if (!r.ok) return;
      const d = await r.json();
      const ack = getAck();
      const unseen = (d.results || []).find((x) => !ack.has(x.room_id));
      if (unseen) setResult(unseen);
    } catch (err) { console.warn("[tournament-result] load failed:", err); }
  }, [user]);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const dismiss = () => {
    if (!result) return;
    const ack = getAck(); ack.add(result.room_id); saveAck(ack);
    setResult(null);
    setDismissed(true);
  };

  if (!result || dismissed) return null;

  const mr = result.my_result || {};
  const outcome = mr.outcome;

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4" data-testid="tournament-result-modal">
      <div className="w-full max-w-md rounded-sm border border-primary/40 bg-gradient-to-b from-[#050a18] to-[#020408] p-6 space-y-4 text-center">
        {outcome === "won" && mr.prize_cents > 0 && (
          <>
            <Trophy className="h-12 w-12 mx-auto text-amber-300" />
            <p className="font-mono text-[10px] tracking-[0.3em] text-amber-200">// VITÓRIA CÓSMICA</p>
            <h2 className="font-display font-black text-4xl tracking-tighter text-amber-200">{mr.rank}º LUGAR!</h2>
            <p className="text-sm text-muted-foreground">Você ganhou na sala <span className="text-white">{result.label}</span></p>
            <div className="rounded-sm border border-amber-300/40 bg-amber-300/10 p-4">
              <p className="font-mono text-[10px] tracking-widest text-amber-200/70">PRÊMIO</p>
              <p className="font-display font-black text-5xl tracking-tighter text-amber-200" data-testid="result-prize">{fmtUsd(mr.prize_cents)}</p>
              <p className="font-mono text-[9px] tracking-widest text-amber-200/70 mt-2">USDT</p>
            </div>
            <div className="text-left font-mono text-xs text-muted-foreground border border-primary/20 rounded-sm p-3 space-y-1">
              <p className="flex items-start gap-2"><Info className="h-3 w-3 mt-0.5 shrink-0 text-emerald-300" /> Creditado na sua <span className="text-emerald-300">carteira interna</span></p>
              <p>Você já pode usar esse saldo para entrar em outras salas — ou sacar em USDT pelo botão SACAR na /carteira.</p>
            </div>
          </>
        )}
        {outcome === "played" && (
          <>
            <div className="h-12 w-12 mx-auto rounded-full border-2 border-primary flex items-center justify-center">
              <span className="font-black text-xl text-primary">{mr.rank}º</span>
            </div>
            <p className="font-mono text-[10px] tracking-[0.3em] text-primary">// SALA ENCERRADA</p>
            <h2 className="font-display font-black text-2xl tracking-tighter text-white">{mr.rank}º lugar</h2>
            <p className="text-sm text-muted-foreground">Você ficou fora do top 10 na sala <span className="text-white">{result.label}</span></p>
            <p className="text-xs font-mono text-muted-foreground">Só o top 10 recebe prêmio. Bora de novo?</p>
          </>
        )}
        {outcome === "cancelled" && (
          <>
            <XCircle className="h-12 w-12 mx-auto text-secondary" />
            <p className="font-mono text-[10px] tracking-[0.3em] text-secondary">// SALA CANCELADA</p>
            <h2 className="font-display font-black text-2xl tracking-tighter text-white">Menos de 15 jogadores</h2>
            <p className="text-sm text-muted-foreground">A sala <span className="text-white">{result.label}</span> não teve jogadores suficientes.</p>
            <div className="rounded-sm border border-emerald-500/40 bg-emerald-500/10 p-4">
              <p className="font-mono text-[10px] tracking-widest text-emerald-300">REEMBOLSO CREDITADO</p>
              <p className="font-display font-black text-3xl text-emerald-300">{fmtUsd(mr.refunded_cents)}</p>
            </div>
          </>
        )}
        {outcome === "no_show" && (
          <>
            <p className="font-mono text-[10px] tracking-[0.3em] text-muted-foreground">// SALA ENCERRADA</p>
            <h2 className="font-display font-black text-2xl tracking-tighter text-white">Sem pontuação</h2>
            <p className="text-sm text-muted-foreground">Você entrou na sala <span className="text-white">{result.label}</span> mas não pontuou. Sem prêmio.</p>
          </>
        )}
        <button onClick={dismiss} data-testid="tournament-result-dismiss" className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-mono tracking-widest text-xs px-5 py-3">
          <X className="h-3.5 w-3.5 inline mr-1" /> FECHAR
        </button>
      </div>
    </div>
  );
}
